import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import { ZIndex } from '@rover/kibble/styles';
import { useTheme } from '@rover/kibble/themes';
import NavbarBH from '@rover/pages/src/bright-horizons/GlobalUI/Navbar';
import { NavbarBannerBHSSR } from '@rover/pages/src/bright-horizons/GlobalUI/NavbarBannerBH';
import ErrorBoundary from '@rover/react-lib/src/components/errorHandling/ErrorBoundary';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import useIsBrightHorizonsNavigation from '@rover/shared/js/hooks/useIsBrightHorizonsNavigation';

import { useBHNavbarContext } from '../context/BHNavbarContext';

import DesktopOurServices from './menus/DesktopMenu/DesktopOurServices';
import { fireEventForClickingOurServices } from './menus/utilities';
import { MobileMenu } from './menus';
import NavBarLeft from './NavBarLeft';
import NavBarRight from './NavBarRight';

const NavBar: FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isOurServicesDropdownOpen, setIsOurServicesDropdownOpen] = useState(false);
  const { navBarRef } = useBHNavbarContext();

  const theme = useTheme();

  const route = usePageContext();
  const { navBarIsFullWidth } = route;

  const closeMobileMenu = useCallback((): void => {
    setIsMobileMenuOpen(false);
  }, []);

  const toggleMobileMenu = (): void => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDesktopOurServices = (): void => {
    setIsOurServicesDropdownOpen(!isOurServicesDropdownOpen);
    fireEventForClickingOurServices();
  };
  const isBrightHorizonsNavigation = useIsBrightHorizonsNavigation();

  const defaultNavBarStyles = {
    borderBottom: `1px solid ${theme.colors.border.primary.toString()}`,
  };

  const brightHorizonsNavBarStyles = {
    top: '0',
    left: '0',
    width: '100%',
  };

  // Handles document event listener
  useEffect(() => {
    const eventListenerArgs = ['click', closeMobileMenu, false] as const;
    if (isMobileMenuOpen) {
      // we need the setTimeout to ensure the listener is added after the click is handled
      setTimeout(() => {
        document.addEventListener(...eventListenerArgs);
      }, 0);
    } else {
      document.removeEventListener(...eventListenerArgs);
    }
    // return cleanup function
    return () => document.removeEventListener(...eventListenerArgs);
  }, [isMobileMenuOpen, closeMobileMenu]);

  const navbarFinalStyles = useMemo(() => {
    return isBrightHorizonsNavigation ? brightHorizonsNavBarStyles : defaultNavBarStyles;
  }, [isBrightHorizonsNavigation]);

  return (
    <ErrorBoundary location="NavBar">
      <Box
        ref={navBarRef}
        zIndex={ZIndex.NAVBAR.toString()}
        background="navigation"
        position={isBrightHorizonsNavigation ? 'fixed' : 'relative'}
        {...navbarFinalStyles}
      >
        <Section
          role="navigation"
          width="100%"
          zIndex={ZIndex.ONE.toString()}
          contentWidth={navBarIsFullWidth ? 'full' : 'page'}
        >
          {isBrightHorizonsNavigation ? (
            <NavbarBH onToggleMobileMenu={toggleMobileMenu} isMobileMenuOpen={isMobileMenuOpen} />
          ) : (
            <Flex justifyContent="space-between" alignItems="stretch" minHeight="60px" gap="10x">
              <NavBarLeft
                onToggleOurServices={toggleDesktopOurServices}
                isOurServicesDropdownOpen={isOurServicesDropdownOpen}
              />
              <NavBarRight
                onToggleMobileMenu={toggleMobileMenu}
                isMobileMenuOpen={isMobileMenuOpen}
              />
            </Flex>
          )}
        </Section>
        {!isBrightHorizonsNavigation && (
          <DesktopOurServices showServices={isOurServicesDropdownOpen} />
        )}
        <NavbarBannerBHSSR />
        <MobileMenu showMenu={isMobileMenuOpen} />
      </Box>
    </ErrorBoundary>
  );
};

export default NavBar;
